/**
 * useActiveOrg — the active workspace's roster and the signed-in user's role in
 * it, shared by every surface that gates on Owner vs Member (the nav + route
 * guard in AuthedLayout, MembersPanel, DomainPanel).
 *
 * All callers share one query key (qk.org) so the roster is fetched once and a
 * single invalidate() after a membership change refreshes all of them.
 *
 * This is the UI half of the boundary only. The server enforces it for real:
 * Better Auth org access control on /api/auth/organization/*, and owner checks
 * on Brain's own endpoints.
 */
import { useQuery } from '@tanstack/react-query';
import { authClient } from '@/lib/auth-client';
import { qk } from '@/lib/query-client';

export interface OrgMember {
  id: string;
  role: string;
  userId: string;
  user: { name: string; email: string };
}

export interface OrgInvitation {
  id: string;
  email: string;
  status: string;
}

export function useActiveOrg() {
  const { data: session } = authClient.useSession();
  const activeOrgId = session?.session.activeOrganizationId ?? null;
  const meId = session?.user.id;

  // getFullOrganization returns the active org with its members and invitations.
  const query = useQuery({
    queryKey: qk.org(activeOrgId),
    enabled: Boolean(activeOrgId),
    queryFn: async () => {
      const { data, error } = await authClient.organization.getFullOrganization();
      if (error) throw new Error(error.message ?? 'Failed to load members');
      return data as unknown as { members: OrgMember[]; invitations: OrgInvitation[] } | null;
    },
  });

  const members = query.data?.members ?? [];
  const myRole = members.find((m) => m.userId === meId)?.role ?? null;

  return {
    activeOrgId,
    meId,
    members,
    invitations: query.data?.invitations ?? [],
    isOwner: myRole === 'owner',
    /**
     * False until the roster has resolved. Gate redirects on this — isOwner
     * reads false while loading, which would bounce an owner off an owner-only
     * page on a cold page load.
     */
    roleKnown: Boolean(activeOrgId) && query.isSuccess,
    isLoading: query.isLoading,
  };
}
